import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, StatusPill, formatCurrency } from '@careandkey/shared';
import { supabase } from '../lib/supabase';
import { useOrg } from '../lib/org';
import { Layout, Topbar } from '../components/Layout';
import { fmtDate } from '../lib/fmt';

type Stmt = { id: string; statement_number: string | null; property_id: string | null; period_start: string; period_end: string; status: string; currency: string; interventions_count: number; billable_costs_total: number; invoiced_total: number; paid_total: number; balance: number };
type Line = { id: string; section: string; line_date: string | null; description: string | null; amount: number };

export function Statements() {
  const { t, i18n } = useTranslation();
  const { currency } = useOrg();
  const loc = i18n.language;
  const year = new Date().getFullYear();
  const [props, setProps] = useState<{ id: string; name: string }[]>([]);
  const [stmts, setStmts] = useState<Stmt[]>([]);
  const [propId, setPropId] = useState('');
  const [month, setMonth] = useState(`${year}-01`);
  const [sel, setSel] = useState<string | null>(null);
  const [lines, setLines] = useState<Line[]>([]);
  const money = (n: number) => formatCurrency(n, currency, loc);

  const load = useCallback(async () => {
    const [pl, st] = await Promise.all([
      supabase.from('v_pnl_lines').select('property_id,property_name'),
      supabase.from('owner_statements').select('*').is('deleted_at', null).order('period_start', { ascending: false }),
    ]);
    const m = new Map<string, string>();
    for (const r of (pl.data as any[]) ?? []) if (r.property_id) m.set(r.property_id, r.property_name);
    setProps([...m.entries()].map(([id, name]) => ({ id, name })));
    setStmts((st.data as any) ?? []);
  }, []);
  useEffect(() => { void load(); }, [load]);
  const propName = useMemo(() => new Map(props.map((p) => [p.id, p.name])), [props]);

  async function generate() {
    if (!propId) return;
    const start = `${month}-01`;
    const end = new Date(Number(month.slice(0, 4)), Number(month.slice(5, 7)), 0).toISOString().slice(0, 10);
    const r = await supabase.rpc('generate_owner_statement', { p_property: propId, p_period_start: start, p_period_end: end });
    if (r.data) { setSel(r.data as string); }
    await load();
  }
  const openStmt = useCallback(async (id: string) => {
    setSel(id);
    const { data } = await supabase.from('owner_statement_lines').select('id,section,line_date,description,amount').eq('statement_id', id).is('deleted_at', null).order('line_date');
    setLines((data as any) ?? []);
  }, []);
  useEffect(() => { if (sel) void openStmt(sel); }, [sel, openStmt]);

  async function publish(id: string) { await supabase.rpc('publish_owner_statement', { p_statement: id }); await load(); }

  const current = stmts.find((s) => s.id === sel);
  const sections = ['intervention', 'billable_cost', 'invoice', 'payment'];

  return (
    <Layout header={<Topbar />}>
      <div className="px-7 py-6">
        <h1 className="mb-1 text-xl font-semibold">{t('nav.statements')}</h1>
        <p className="mb-5 text-sm text-muted">{t('stm.subtitle')}</p>

        <Card className="mb-5 flex flex-wrap items-end gap-3 p-4">
          <label className="flex flex-col gap-1 text-sm"><span className="text-xs text-muted">{t('stm.property')}</span>
            <select value={propId} onChange={(e) => setPropId(e.target.value)} className="min-w-[200px] rounded-md border border-border bg-card px-2 py-1.5">
              <option value="">{t('stm.selectProperty')}</option>
              {props.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-sm"><span className="text-xs text-muted">{t('stm.month')}</span>
            <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="rounded-md border border-border bg-card px-2 py-1.5" />
          </label>
          <Button onClick={generate} disabled={!propId}>{t('stm.generate')}</Button>
        </Card>

        <div className="grid grid-cols-[1fr_1.3fr] gap-5">
          <Card className="overflow-hidden p-0">
            <div className="border-b border-border px-4 py-2.5 text-sm font-medium">{t('stm.list')}</div>
            <div className="divide-y divide-divider">
              {stmts.map((s) => (
                <button key={s.id} onClick={() => setSel(s.id)} className={`flex w-full items-center justify-between px-4 py-2.5 text-left text-sm ${sel === s.id ? 'bg-bg' : ''}`}>
                  <div><div className="font-medium">{propName.get(s.property_id ?? '') ?? '—'}</div><div className="text-xs text-muted">{fmtDate(s.period_start, loc)} · {s.statement_number ?? t('stm.draft')}</div></div>
                  <StatusPill tone={s.status === 'published' ? 'done' : 'open'}>{t('stm.st_' + s.status)}</StatusPill>
                </button>
              ))}
              {stmts.length === 0 && <div className="px-4 py-8 text-center text-muted">{t('common.noData')}</div>}
            </div>
          </Card>

          <Card className="p-0">
            {!current && <div className="p-6 text-sm text-muted">{t('stm.selectHint')}</div>}
            {current && (
              <div>
                <div className="flex items-center justify-between border-b border-border px-5 py-3">
                  <div><div className="font-semibold">{propName.get(current.property_id ?? '') ?? '—'}</div><div className="text-xs text-muted">{fmtDate(current.period_start, loc)} – {fmtDate(current.period_end, loc)}</div></div>
                  <div className="flex items-center gap-2">
                    <a href={`/statements/${current.id}/print`} target="_blank" rel="noreferrer" className="rounded-md border border-border px-3 py-1.5 text-sm">{t('stm.pdf')}</a>
                    {current.status === 'draft' && <Button onClick={() => publish(current.id)}>{t('stm.publish')}</Button>}
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-px bg-divider">
                  {[['interventions', current.interventions_count], ['billableCosts', current.billable_costs_total], ['invoiced', current.invoiced_total], ['balance', current.balance]].map(([k, v], i) => (
                    <div key={k as string} className="bg-card px-4 py-3">
                      <div className="text-2xs uppercase text-muted">{t('stm.' + k)}</div>
                      <div className="font-mono text-sm">{i === 0 ? v : money(v as number)}</div>
                    </div>
                  ))}
                </div>
                <div className="divide-y divide-divider">
                  {sections.map((sec) => {
                    const ls = lines.filter((l) => l.section === sec);
                    if (!ls.length) return null;
                    return (
                      <div key={sec} className="px-5 py-3">
                        <div className="mb-1 text-xs font-medium uppercase tracking-wide text-muted">{t('stm.sec_' + sec)}</div>
                        {ls.map((l) => (
                          <div key={l.id} className="flex justify-between py-0.5 text-sm">
                            <span className="text-muted">{l.line_date && fmtDate(l.line_date, loc)} · {l.description}</span>
                            {sec !== 'intervention' && <span className="font-mono">{money(Number(l.amount))}</span>}
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>
    </Layout>
  );
}
